import React from 'react';
import { ArchiveIcon, ArchiveRestoreIcon, BellIcon, UserPlus2Icon } from 'lucide-react';
import type { LeadLabel } from '../types/leads';
interface Chat {
  id: string;
  name: string;
  phone: string;
  status: 'lead' | 'active' | 'past' | 'cold';
  lastMessage: string;
  timestamp: string;
  timestampMs: number | null;
  hasReminder: boolean;
  unread: boolean;
  avatar?: string;
  kind?: 'lead' | 'client';
  leadLabel?: LeadLabel;
  medium?: 'online' | 'offline';
}
interface ChatRowProps {
  chat: Chat;
  unreadCount?: number;
  isOffline?: boolean;
  onConvert?: () => void;
  converting?: boolean;
  onArchive?: () => void;
  archiving?: boolean;
  onUnarchive?: () => void;
  unarchiving?: boolean;
}
export function ChatRow({
  chat,
  unreadCount = 0,
  isOffline = false,
  onConvert,
  converting,
  onArchive,
  archiving,
  onUnarchive,
  unarchiving
}: ChatRowProps) {
  const statusColors = {
    lead: 'bg-[#C9A870] text-white',
    active: 'bg-[#5B8E87] text-white',
    past: 'bg-gray-400 text-white',
    cold: 'bg-blue-500 text-white'
  } as const;
  const leadLabelColors: Record<LeadLabel, string> = {
    hot: 'bg-red-100 text-red-700 border border-red-200',
    warm: 'bg-orange-100 text-orange-700 border border-orange-200',
    cold: 'bg-blue-100 text-blue-700 border border-blue-200',
    new: 'bg-gray-100 text-gray-600 border border-gray-200'
  };
  const mediumColors = {
    online: 'bg-blue-100 text-blue-700 border border-blue-200',
    offline: 'bg-amber-100 text-amber-700 border border-amber-200'
  } as const;
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Format phone number for display (e.g., "+91 98765 43210")
  const formatPhoneNumber = (phone: string) => {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // If it's an Indian number (starts with 91 and has 12 digits)
    if (digits.startsWith('91') && digits.length === 12) {
      return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
    }

    // Generic format: add + prefix if not present, add spaces every 3-4 digits
    const formatted = digits.replace(/(\d{1,3})(\d{3,5})?(\d{4})?/, (_match, p1, p2, p3) => {
      let result = p1;
      if (p2) result += ' ' + p2;
      if (p3) result += ' ' + p3;
      return result;
    });

    return digits.length > 10 ? `+${formatted}` : formatted;
  };

  // Use formatted phone as display name if name is missing or looks like a number
  const displayName = !chat.name || /^[\d\s\+\-\(\)]+$/.test(chat.name)
    ? formatPhoneNumber(chat.phone)
    : chat.name;

  const isLead = chat.kind === 'lead';
  const activeLeadLabel: LeadLabel = chat.leadLabel ?? 'new';

  const statusLabels = {
    lead: 'Lead',
    active: 'Active',
    past: 'Inactive',
    cold: 'Cold'
  } as const;

  const badgeLabel = isLead
    ? `${activeLeadLabel.charAt(0).toUpperCase()}${activeLeadLabel.slice(1)} Lead`
    : statusLabels[chat.status];
  const badgeClasses = isLead
    ? leadLabelColors[activeLeadLabel]
    : statusColors[chat.status];
  return <div className={`rounded-lg shadow-sm p-2.5 flex items-center gap-2.5 cursor-pointer hover:shadow-md transition-all ${chat.unread ? 'bg-[#F0FDF4] border-l-4 border-l-[#5B8E87] border-y border-r border-gray-100' : 'bg-white border border-gray-100'}`}>
      <div className="w-10 h-10 bg-[#5B8E87] bg-opacity-10 rounded-full flex items-center justify-center flex-shrink-0">
        {chat.avatar ? <img src={chat.avatar} alt={displayName} className="w-10 h-10 rounded-full object-cover" /> : <span className="text-[#5B8E87] font-semibold">
            {getInitials(displayName)}
          </span>}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className={`text-sm font-semibold text-[#212121] truncate mb-0.5 ${chat.unread ? 'font-bold' : ''}`}>
          {displayName}
        </h3>
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${badgeClasses}`}>
            {badgeLabel}
          </span>
          {chat.medium && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${mediumColors[chat.medium]}`}>
              {chat.medium === 'online' ? 'Online' : 'Offline'}
            </span>
          )}
        </div>
        <p className={`text-xs truncate ${chat.unread ? 'text-[#212121] font-medium' : 'text-gray-600'}`}>
          {chat.lastMessage}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {chat.kind === 'lead' && (
          <div className="flex gap-1">
            {onConvert && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onConvert();
                }}
                disabled={converting}
                className={`p-1 rounded-md transition-colors ${
                  converting
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-[#5B8E87] text-white hover:bg-[#4F7A74]'
                }`}
                title="Convert to Client"
              >
                <UserPlus2Icon className="w-3.5 h-3.5" />
              </button>
            )}
            {onArchive && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive();
                }}
                disabled={archiving}
                className={`p-1 rounded-md transition-colors ${
                  archiving
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
                title="Archive Lead"
              >
                <ArchiveIcon className="w-3.5 h-3.5" />
              </button>
            )}
            {onUnarchive && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUnarchive();
                }}
                disabled={unarchiving}
                className={`p-1 rounded-md transition-colors ${
                  unarchiving
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
                title="Unarchive Lead"
              >
                <ArchiveRestoreIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        <div className="flex flex-col items-end gap-0">
          <span className={`text-[10px] ${chat.unread ? 'text-[#5B8E87] font-semibold' : 'text-gray-500'}`}>
            {chat.timestamp}
          </span>
          {isOffline && chat.timestampMs && (
            <span className="text-[9px] text-gray-400">
              Offline
            </span>
          )}
        </div>
        {chat.hasReminder && <BellIcon className="w-4 h-4 text-gray-700" />}
        {chat.unread && unreadCount > 0 && <div className="min-w-5 h-5 px-1.5 bg-[#5B8E87] rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">{unreadCount > 99 ? '99+' : unreadCount}</span>
          </div>}
      </div>
    </div>;
}
